import { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CircularProgress from '@mui/material/CircularProgress';
import GlobalStyles from '@mui/material/GlobalStyles';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import logo from '../assets/img/logo.png';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import config from '../config';
import { isAccessExpired } from '../utils/accessControl';
import AccessExpiredDialog from '../dialog/AccessExpiredDialog';

import { SvgIcon } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import GrainIcon from '@mui/icons-material/Grain';
import MapIcon from '@mui/icons-material/Map';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { paperClasses } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import MailOutlineIcon from '@mui/icons-material/MailOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import backgroundImage from '../assets/img/background.webp';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

// #8 — shared focus-glow style applied to both text fields
const fieldFocusGlow = {
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0174BE',
    boxShadow: '0 0 0 3px rgba(1, 116, 190, 0.12)',
  },
};

function Copyright(props) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {'© 2026 Mirukuru. All rights reserved.'}
    </Typography>
  );
}

// Exchange Cognito tokens for a Strapi JWT and fetch the Strapi user profile.
async function fetchStrapiUser(idToken, accessToken) {
  const callback = await axios.get(
    `${config.apiUrl}/api/auth/cognito/callback?id_token=${idToken}&access_token=${accessToken}`
  );
  const strapiJwt = callback.data.jwt;
  const me = await axios.get(`${config.apiUrl}/api/users/me?populate=*`, {
    headers: { Authorization: `Bearer ${strapiJwt}` },
  });
  return me.data;
}

export default function Login() {
  const { i18n, t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { signIn, signOut, setService, setAdminMode } = useContext(AuthContext);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [showModeDialog, setShowModeDialog] = useState(false);
  const [userService, setUserService] = useState('');
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);   // #6
  const [shakeError, setShakeError] = useState(false);        // #7
  const [showAccessExpiredDialog, setShowAccessExpiredDialog] = useState(false);
  const [expiredPlan, setExpiredPlan] = useState(undefined);

  useEffect(() => {
    if (localStorage.getItem('languageMirukuru') !== null) {
      i18n.changeLanguage(localStorage.getItem('languageMirukuru'));
    }
  }, [i18n]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const data = new FormData(e.currentTarget);
    try {
      const { isAdmin, service, idToken, accessToken } = await signIn(
        data.get('email'),
        data.get('password')
      );
      setUserService(service);

      // Administrators are always exempt from the access window.
      if (isAdmin) {
        setIsLoading(false);
        setShowModeDialog(true);
        return;
      }

      // Non-admin: enforce the access window before entering the app.
      let expired = false;
      let expiredUserPlan;
      try {
        const strapiUser = await fetchStrapiUser(idToken, accessToken);
        expired = isAccessExpired(strapiUser);
        expiredUserPlan = strapiUser?.plan;
      } catch (checkErr) {
        // Fail open: a Strapi hiccup must not lock users out (backend enforcement comes later).
        console.log('access check failed, allowing login', checkErr);
      }

      setIsLoading(false);

      if (expired) {
        setExpiredPlan(expiredUserPlan);
        setShowAccessExpiredDialog(true);
        await signOut(); // clear the Cognito session so the app cannot be re-entered
        return;
      }

      dashboardAccessWithService(service);
    } catch (err) {
      console.log(err.message);
      enqueueSnackbar(`${t('ErrorLogin')}`, { variant: 'error' });
      setIsLoading(false);
      // #7 — trigger shake animation, then reset so it can fire again next error
      setShakeError(true);
      setTimeout(() => setShakeError(false), 600);
    }
  };

  function dashboardAccessWithService(service) {
    if (service === 'classic') {
      setService('classic');
      navigate('/');
    }
    if (service === 'mapping') {
      setService('mapping');
      navigate('/');
    }
    if (service === 'all') {
      setShowServiceDialog(true);
    }
  }

  const modeDialog = (
    <Dialog open={showModeDialog} onClose={() => setShowModeDialog(false)}>
      <Box sx={{ p: 3 }}>
        <DialogTitle>{t('ChooseYourPanel')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('ChooseYourPanelDescription')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<SvgIcon fontSize="medium"><AdminPanelSettingsIcon /></SvgIcon>}
            variant="contained"
            color="primary"
            onClick={() => {
              setShowModeDialog(false);
              setService('classic');
              setAdminMode(true);
              navigate('/admin');
            }}
            autoFocus
          >
            {t('Administrator')}
          </Button>
          <Button
            startIcon={<SvgIcon fontSize="medium"><PersonIcon /></SvgIcon>}
            variant="contained"
            color="primary"
            onClick={() => {
              setShowModeDialog(false);
              dashboardAccessWithService(userService);
            }}
          >
            {t('User')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );

  const serviceDialog = (
    <Dialog open={showServiceDialog} onClose={() => setShowServiceDialog(false)}>
      <Box sx={{ p: 3 }}>
        <DialogTitle>{t('ChooseYourService')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('ChooseYourServiceDescription')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<SvgIcon fontSize="medium"><GrainIcon /></SvgIcon>}
            variant="contained"
            color="primary"
            onClick={() => {
              setShowServiceDialog(false);
              setService('classic');
              navigate('/');
            }}
            autoFocus
          >
            {t('ServiceClassic')}
          </Button>
          <Button
            startIcon={<SvgIcon fontSize="medium"><MapIcon /></SvgIcon>}
            variant="contained"
            color="primary"
            onClick={() => {
              setShowServiceDialog(false);
              setService('mapping');
              navigate('/');
            }}
          >
            {t('ServiceMapping')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );

  const content = (
    <Grid
      container
      spacing={0}
      direction="row"
      style={{ minHeight: '100vh' }}
      sx={{ px: 3, maxWidth: '700px', alignItems: 'center', justifyContent: 'center' }}
    >
      <Grid size={12}>
        {/* #3 — entrance animation wrapper */}
        <Box sx={{ animation: 'cardEntrance 0.6s ease-out forwards' }}>
          <Card
            // #1 — inline style bypasses MUI Paper's theme background-color class.
            // overflow must NOT be hidden — it breaks backdrop-filter in most browsers.
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.89)' }}
            sx={{
              borderRadius: '50px',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.65)',
              // #7 — shake on error
              animation: shakeError ? 'shake 0.5s ease-in-out' : 'none',
              [`&.${paperClasses.elevation1}`]: {
                boxShadow: '0 20px 80px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255,255,255,0.15)',
              },
              '&:hover': {
                boxShadow: '0 20px 80px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255,255,255,0.15)',
                transform: 'none',
              },
            }}
          >
            <Box sx={{ p: { xs: 2, sm: 3, md: 5 } }}>
              <Stack spacing={4} sx={{ mx: 4, mt:4 }}>
                <CardMedia
                  component="img"
                  alt="logo"
                  image={logo}
                  sx={{ maxWidth: '400px', minWidth: '150px', mx: 'auto', filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.1))' }}
                />

                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ pt: 2 }}>
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label={t('Email')}
                      name="email"
                      type="email"
                      sx={fieldFocusGlow}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <MailOutlineIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />

                    <TextField
                      fullWidth
                      label={t('Password')}
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      sx={fieldFocusGlow}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlinedIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(v => !v)}
                                edge="end"
                                size="small"
                                tabIndex={-1}
                                aria-label="toggle password visibility"
                              >
                                {showPassword
                                  ? <VisibilityOff fontSize="small" />
                                  : <Visibility fontSize="small" />
                                }
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Stack>

                  <Button
                    fullWidth
                    size="large"
                    sx={{ mt: 3 }}
                    type="submit"
                    variant="contained"
                    disabled={isLoading}
                  >
                    {isLoading
                      ? <CircularProgress size={22} color="inherit" />
                      : t('Login')
                    }
                  </Button>
                </Box>

                <Stack spacing={1} sx={{ pt: 2, alignItems: 'center' }}>
                  <Link
                    onClick={() => window.location.href = '/reset-password'}
                    underline="hover"
                    variant="subtitle1"
                  >
                    {t('ForgotPassword')}
                  </Link>
                  <Typography color="text.secondary" variant="body1" align="center">
                    {t('DontHaveAccount')}
                    &nbsp;
                    <Link
                      onClick={() => window.location.href = 'mailto:contact-user-guest@mirukuru.co.jp'}
                      underline="hover"
                      variant="subtitle1"
                    >
                      {t('ContactUs')}
                    </Link>
                  </Typography>
                </Stack>

                <Box sx={{ pt: 3 }}>
                  <Copyright />
                </Box>
              </Stack>
            </Box>
          </Card>
        </Box>
      </Grid>
    </Grid>
  );

  return (
    <>
      {/* CSS keyframes for all animations */}
      <GlobalStyles styles={`
        @keyframes kenBurns {
          0%   { transform: scale(1)    translate(0, 0); }
          50%  { transform: scale(1.06) translate(-1%, -0.5%); }
          100% { transform: scale(1)    translate(0, 0); }
        }
        @keyframes cardEntrance {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes shake {
          0%,  100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
          20%, 40%, 60%, 80%      { transform: translateX(8px); }
        }
      `} />

      {modeDialog}
      {serviceDialog}
      <AccessExpiredDialog
        open={showAccessExpiredDialog}
        plan={expiredPlan}
        onClose={() => setShowAccessExpiredDialog(false)}
      />

      <Box sx={{ position: 'relative', minHeight: '100vh', width: '100%', overflow: 'hidden' }}>
        {/* #2 — Ken Burns animated background layer */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            animation: 'kenBurns 20s ease-in-out infinite',
            zIndex: 0,
          }}
        />
        {/* Dark overlay for card contrast */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.01)',
            zIndex: 1,
          }}
        />
        {/* Content layer */}
        <Grid
          container
          direction="row"
          sx={{ position: 'relative', zIndex: 2, justifyContent: 'center', alignItems: 'center' }}
        >
          {content}
        </Grid>
      </Box>
    </>
  );
}
